import type { StocktakeDetail } from '@erp/shared';
import { ArrowLeft, ClipboardCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog, Input } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatDecimals } from '../../lib/money.js';

const STATUS_TONE: Record<string, 'success' | 'secondary' | 'danger' | 'default'> = {
  posted: 'success', draft: 'secondary', cancelled: 'danger',
};

export function StocktakeDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation('stocktake');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';
  const canWrite = usePermission('stock.adjust');
  const [doc, setDoc] = useState<StocktakeDetail | null>(null);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const hydrate = (d: StocktakeDetail): void => {
    setDoc(d);
    setCounts(Object.fromEntries(d.lines.map((l) => [l.id, l.countedQty ?? ''])));
  };
  useEffect(() => { void api().get<StocktakeDetail>(`/stocktakes/${id}`).then((r) => hydrate(r.data)); }, [id]);

  const isDraft = doc?.status === 'draft';

  // Live valued variance using the entered counts (draft) or stored values.
  const totalVariance = useMemo(() => {
    if (!doc) return 0;
    return doc.lines.reduce((sum, l) => {
      const counted = isDraft ? counts[l.id] : l.countedQty;
      if (counted === undefined || counted === null || counted === '') return sum;
      return sum + (Number(counted) - Number(l.systemQty)) * Number(l.avgCost);
    }, 0);
  }, [doc, counts, isDraft]);

  if (!doc) return <Card className="p-6"><Skeleton className="h-64 w-full" /></Card>;

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      const payload = doc.lines.map((l) => { const v = counts[l.id]?.trim(); return { lineId: l.id, countedQty: v ? v : null }; });
      const r = await api().put<StocktakeDetail>(`/stocktakes/${id}/counts`, { counts: payload });
      hydrate(r.data);
      toast.success(t('saved', { defaultValue: 'Counts saved.' }));
    } catch { toast.error(t('saveFailed', { defaultValue: 'Could not save counts.' })); } finally { setBusy(false); }
  };

  const post = async (): Promise<void> => {
    if (!(await confirmDialog({ title: t('postConfirm', { defaultValue: 'Post this count?' }), message: t('postConfirmHint', { defaultValue: 'Counted lines will adjust stock to match. This cannot be undone.' }), confirmLabel: t('post', { defaultValue: 'Post' }) }))) return;
    setBusy(true);
    try {
      // Save the latest entries first so nothing typed is lost on post.
      const payload = doc.lines.map((l) => { const v = counts[l.id]?.trim(); return { lineId: l.id, countedQty: v ? v : null }; });
      await api().put(`/stocktakes/${id}/counts`, { counts: payload });
      const r = await api().post<StocktakeDetail>(`/stocktakes/${id}/post`);
      hydrate(r.data);
      toast.success(t('posted', { defaultValue: 'Count posted — stock adjusted.' }));
    } catch { toast.error(t('postFailed', { defaultValue: 'Could not post the count.' })); } finally { setBusy(false); }
  };

  const cancel = async (): Promise<void> => {
    if (!(await confirmDialog({ title: t('cancelConfirm', { defaultValue: 'Discard this count?' }), message: t('cancelConfirmHint', { defaultValue: 'The draft count will be discarded. Stock is not changed.' }), tone: 'danger', confirmLabel: t('discard', { defaultValue: 'Discard' }) }))) return;
    try { await api().delete(`/stocktakes/${id}`); navigate('/inventory/stocktakes'); }
    catch { toast.error(t('cancelFailed', { defaultValue: 'Could not discard the count.' })); }
  };

  const liveVariance = (lineId: string, systemQty: string): number | null => {
    const v = counts[lineId];
    if (v === undefined || v.trim() === '') return null;
    return Number(v) - Number(systemQty);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/inventory/stocktakes" className="mb-1 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"><ArrowLeft className="h-3.5 w-3.5" aria-hidden />{t('back', { defaultValue: 'All counts' })}</Link>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <ClipboardCheck className="h-6 w-6 text-brand" aria-hidden /><span className="font-mono">{doc.docNumber}</span>
            <Badge variant={STATUS_TONE[doc.status] ?? 'default'}>{t(`status.${doc.status}`, { defaultValue: doc.status })}</Badge>
          </h1>
          <p className="mt-1 text-sm text-fg-muted">{doc.warehouseName}{doc.note ? ` · ${doc.note}` : ''}</p>
        </div>
        {isDraft && canWrite && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={save} loading={busy}>{t('save', { defaultValue: 'Save counts' })}</Button>
            <Button onClick={post} loading={busy}>{t('post', { defaultValue: 'Post' })}</Button>
            <Button variant="ghost" onClick={cancel}>{t('discard', { defaultValue: 'Discard' })}</Button>
          </div>
        )}
      </div>

      <Card className="flex flex-wrap gap-x-8 gap-y-2 p-4 text-sm">
        <div><span className="text-fg-muted">{t('columns.counted', { defaultValue: 'Counted' })}: </span><span className="font-mono">{doc.lines.filter((l) => (isDraft ? counts[l.id]?.trim() : l.countedQty)).length}/{doc.lines.length}</span></div>
        <div><span className="text-fg-muted">{t('columns.variance', { defaultValue: 'Variance value' })}: </span><span className={`font-mono ${totalVariance < 0 ? 'text-danger' : totalVariance > 0 ? 'text-success' : ''}`}>{formatDecimals(totalVariance, doc.lines[0]?.avgCostCurrencyCode ?? baseCurrency, i18n.language)}</span></div>
      </Card>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-bg-subtle text-fg-muted">
            <tr>
              <th className="px-4 py-2 text-start font-medium">{t('line.item', { defaultValue: 'Item' })}</th>
              <th className="px-4 py-2 text-end font-medium">{t('line.system', { defaultValue: 'System' })}</th>
              <th className="px-4 py-2 text-end font-medium">{t('line.counted', { defaultValue: 'Counted' })}</th>
              <th className="px-4 py-2 text-end font-medium">{t('line.variance', { defaultValue: 'Variance' })}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {doc.lines.map((l) => {
              const v = isDraft ? liveVariance(l.id, l.systemQty) : (l.countedQty === null ? null : Number(l.varianceQty));
              return (
                <tr key={l.id}>
                  <td className="px-4 py-2"><span className="font-mono text-xs">{l.itemCode}</span> · {l.itemName}</td>
                  <td className="px-4 py-2 text-end font-mono tabular-nums text-fg-muted">{Number(l.systemQty)}</td>
                  <td className="px-4 py-2 text-end">
                    {isDraft && canWrite ? (
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={counts[l.id] ?? ''}
                        onChange={(e) => setCounts((c) => ({ ...c, [l.id]: e.target.value }))}
                        className="ms-auto h-8 w-28 text-end font-mono"
                        aria-label={`${t('line.counted', { defaultValue: 'Counted' })} ${l.itemName}`}
                      />
                    ) : (
                      <span className="font-mono tabular-nums">{l.countedQty === null ? '—' : Number(l.countedQty)}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-end font-mono tabular-nums">
                    {v === null ? <span className="text-fg-subtle">—</span> : <span className={v < 0 ? 'text-danger' : v > 0 ? 'text-success' : 'text-fg-muted'}>{v > 0 ? `+${v}` : v}</span>}
                  </td>
                </tr>
              );
            })}
            {doc.lines.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-fg-muted">{t('noLines', { defaultValue: 'This warehouse has no stocked items to count.' })}</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
